/**
 * Frequently-changing nav state for Main pages (layout, selection, asset,
 * category), so rail components can subscribe to just their own slice
 * instead of re-rendering on every other rail's navigation.
 */
import Addon from '@shared/classes/addon';
import Category from '@shared/classes/category';
import Event from '@shared/classes/event';
import Setting from '@shared/classes/setting';
import { IMainState } from '@shared/types/interfaces/states';
import { MainLayout } from '@shared/types/interfaces/main';
import { RootState } from '@shared/store/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Initial state for the main slice.
 */
const initialState: IMainState = {
  asset: undefined,
  category: undefined,
  layout: {},
  selection: '',
};

const mainSlice = createSlice({
  name: 'main',
  initialState,
  reducers: {
    hydrateMain: (state, action: PayloadAction<Partial<IMainState>>) => {
      Object.assign(state, action.payload);
    },
    resetMain: () => initialState,
    setAsset: (
      state,
      action: PayloadAction<Addon | Event | Setting | undefined>
    ) => {
      state.asset = action.payload;
    },
    setLayout: (state, action: PayloadAction<MainLayout>) => {
      state.layout = action.payload;
    },
    setLayoutIndex: (
      state,
      action: PayloadAction<{ id: string; index: number }>
    ) => {
      const { id, index } = action.payload;
      state.layout[id] = {
        ...state.layout[id],
        hIndex: index,
      };
    },
    setSelection: (state, action: PayloadAction<string>) => {
      state.selection = action.payload;
    },
    /**
     * Combines the asset/category/selection/layout-index writes `useMain.ts`
     * fires per keypress into one dispatch (one subscriber pass instead of
     * up to four).
     */
    setNavigation: (
      state,
      action: PayloadAction<{
        asset?: Addon | Event | Setting;
        category?: Category;
        selection?: string;
        layoutId?: string;
        layoutIndex?: number;
      }>
    ) => {
      const { asset, category, selection, layoutId, layoutIndex } =
        action.payload;
      if (asset !== undefined) state.asset = asset;
      if (category !== undefined) state.category = category;
      if (selection !== undefined) state.selection = selection;
      if (layoutId !== undefined && layoutIndex !== undefined) {
        state.layout[layoutId] = {
          ...state.layout[layoutId],
          hIndex: layoutIndex,
        };
      }
    },
  },
});

const { actions, reducer: mainReducer } = mainSlice;
export const {
  hydrateMain,
  resetMain,
  setAsset,
  setLayout,
  setLayoutIndex,
  setNavigation,
  setSelection,
} = actions;

export const selectMainAsset = (state: RootState) => state.main.asset;
export const selectCategory = (state: RootState) => state.main.category;
export const selectLayout = (state: RootState) => state.main.layout;
export const selectSelection = (state: RootState) => state.main.selection;
export const selectHIndex = (id: string) => (state: RootState) =>
  state.main.layout[id]?.hIndex ?? 0;
export const selectVIndex = (id: string) => (state: RootState) =>
  state.main.layout[id]?.vIndex ?? 0;
export const selectCurrentRailVIndex = (state: RootState) =>
  state.main.layout[state.main.selection]?.vIndex ?? 0;

export default mainReducer;
